import React, { useEffect, useState } from 'react';
import { ArrowRight, ShieldCheck, MessageCircle, CheckCircle2, Clock } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import { serviceOptions, whatsappLink } from '../mock';
import Logo from './Logo';

function pad(n) { return String(n).padStart(2, '0'); }

export default function QuoteCTA() {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: '', whatsapp: '', service: '' });
  const [submitted, setSubmitted] = useState(false);
  const [remaining, setRemaining] = useState(6 * 3600 + 42 * 60 + 15);

  useEffect(() => {
    const t = setInterval(() => setRemaining((r) => (r > 0 ? r - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);

  const h = pad(Math.floor(remaining / 3600));
  const m = pad(Math.floor((remaining % 3600) / 60));
  const s = pad(remaining % 60);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.whatsapp) {
      toast({ title: 'Missing details', description: 'Please enter your name and WhatsApp number.' });
      return;
    }
    setSubmitted(true);
    toast({ title: 'Enquiry received', description: 'Opening WhatsApp so we can continue there.' });
    window.open(whatsappLink(form.service), '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="contact" className="py-12 lg:py-28">
      <div className="section-pad max-w-[1200px] mx-auto">
        <div className="relative rounded-[1.4rem] sm:rounded-[2.2rem] overflow-hidden bg-[#16150f] grid lg:grid-cols-2">
          {/* Left copy */}
          <div className="p-6 sm:p-9 lg:p-14 flex flex-col justify-center relative">
            <Logo variant="white" className="h-7 mb-8" />
            <span className="inline-flex items-center gap-2 text-[0.62rem] font-semibold tracking-widest text-[#ff5a1f] w-max bg-[#ff5a1f]/15 rounded-full px-3 py-1.5">
              <Clock className="w-3.5 h-3.5" /> EXCLUSIVE OFFER — ENDING SOON
            </span>
            <h2 className="font-display mt-5 sm:mt-6 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-[1.05] tracking-tight">
              Protect your <span className="font-serif italic font-normal text-[#ff8a5c]">vehicle</span> today.
            </h2>
            <p className="mt-5 text-white/60 leading-relaxed max-w-md">
              Get a personalised quote for PPF, ceramic coating, wrapping or detailing. Our specialists will reach out with the right recommendation for your vehicle.
            </p>
            <div className="mt-9">
              <p className="text-[0.62rem] font-semibold tracking-widest text-white/40 mb-3">OFFER ENDS IN</p>
              <div className="flex gap-2 sm:gap-3">
                {[[h, 'HRS'], [m, 'MIN'], [s, 'SEC']].map(([v, l]) => (
                  <div key={l} className="flex flex-col items-center bg-white/5 border border-white/10 rounded-2xl px-4 sm:px-5 py-3 min-w-[64px] sm:min-w-[70px]">
                    <span className="font-display text-2xl sm:text-3xl font-extrabold text-white tabular-nums">{v}</span>
                    <span className="text-[0.55rem] tracking-widest text-white/40 mt-1">{l}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="absolute -bottom-20 -left-10 w-64 h-64 bg-[#ff5a1f]/20 blur-[80px] rounded-full" />
          </div>

          {/* Right form */}
          <div className="p-6 sm:p-9 lg:p-14 bg-white lg:m-3 lg:rounded-[1.8rem]">
            {!submitted ? (
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="text-xs font-semibold text-[#8a8578] mb-2 block tracking-wide">YOUR NAME</label>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Enter your name" className="w-full bg-[#faf8f3] border border-black/8 rounded-xl px-4 py-3.5 text-sm text-[#16150f] placeholder:text-[#b5b0a3] focus:outline-none focus:border-[#ff5a1f] focus:ring-2 focus:ring-[#ff5a1f]/20 transition-all" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#8a8578] mb-2 block tracking-wide">WHATSAPP NUMBER</label>
                  <input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+91 00000 00000" className="w-full bg-[#faf8f3] border border-black/8 rounded-xl px-4 py-3.5 text-sm text-[#16150f] placeholder:text-[#b5b0a3] focus:outline-none focus:border-[#ff5a1f] focus:ring-2 focus:ring-[#ff5a1f]/20 transition-all" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#8a8578] mb-2 block tracking-wide">SELECT SERVICE</label>
                  <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                    <SelectTrigger className="w-full bg-[#faf8f3] border-black/8 rounded-xl px-4 py-3.5 h-auto text-sm text-[#16150f] focus:ring-2 focus:ring-[#ff5a1f]/20">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-black/8">
                      {serviceOptions.map((o) => (
                        <SelectItem key={o} value={o} className="text-sm focus:bg-[#faf8f3] focus:text-[#ff5a1f]">{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <button type="submit" className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-sm font-semibold text-white bg-[#ff5a1f] hover:bg-[#16150f] transition-all duration-300">
                  Get My Quote <ArrowRight className="w-4 h-4" />
                </button>
                <p className="flex items-center justify-center gap-2 text-[0.68rem] tracking-widest text-[#a39d8e] pt-1">
                  <ShieldCheck className="w-3.5 h-3.5" /> SECURE • PRIVATE CONVERSATION
                </p>
              </form>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center py-10">
                <div className="w-16 h-16 rounded-full bg-[#ff5a1f]/10 flex items-center justify-center mb-5">
                  <CheckCircle2 className="w-8 h-8 text-[#ff5a1f]" />
                </div>
                <h3 className="font-display text-2xl font-bold text-[#16150f]">Enquiry Received</h3>
                <p className="text-sm text-[#5a5548] mt-2 max-w-xs">We\u2019ll be in touch shortly. Continue the conversation on WhatsApp for a faster response.</p>
                <a href={whatsappLink(form.service)} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-sm font-semibold text-white bg-[#25D366] hover:opacity-90 transition-all">
                  <MessageCircle className="w-4 h-4" /> Continue on WhatsApp
                </a>
                <button onClick={() => { setSubmitted(false); setForm({ name: '', whatsapp: '', service: '' }); }} className="mt-3 text-xs text-[#a39d8e] hover:text-[#16150f]">Close</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
